/**
 * Admin Console API client — Cycle 3b B-16.
 *
 * 3 도메인: LLM Integration / Service Module / User Role
 * apiClient (인증 인터셉터 포함) 사용 — admin endpoint 는 정상 401 시 refresh 흐름 유효.
 */

import { apiClient } from './client';

// ===== LLM Integration =====
export interface LlmIntegrationItem {
  id: string;
  provider: 'openai' | 'anthropic' | 'azure_openai' | 'google' | 'custom';
  endpoint: string;
  modelName: string;
  displayName: string;
  workspaceId?: string | null;  // Cycle 17
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface LlmCreatePayload {
  provider: LlmIntegrationItem['provider'];
  endpoint: string;
  apiKey: string;
  modelName: string;
  displayName: string;
  workspaceId?: string;  // Cycle 17
}

export interface LlmUpdatePayload {
  endpoint?: string;
  apiKey?: string;
  modelName?: string;
  displayName?: string;
  isActive?: boolean;
  workspaceId?: string;  // Cycle 18 — edit row 에서 workspaceId 갱신 (빈 string → null)
}

export async function listLlmIntegrations(): Promise<LlmIntegrationItem[]> {
  const { data } = await apiClient.get<{ items: LlmIntegrationItem[] }>('/admin/llm-integrations');
  return Array.isArray(data?.items) ? data.items : [];
}

export async function createLlmIntegration(payload: LlmCreatePayload): Promise<LlmIntegrationItem> {
  const { data } = await apiClient.post<LlmIntegrationItem>('/admin/llm-integrations', payload);
  return data;
}

export async function updateLlmIntegration(id: string, payload: LlmUpdatePayload): Promise<LlmIntegrationItem> {
  const { data } = await apiClient.put<LlmIntegrationItem>(`/admin/llm-integrations/${id}`, payload);
  return data;
}

export async function deleteLlmIntegration(id: string): Promise<void> {
  await apiClient.delete(`/admin/llm-integrations/${id}`);
}

// ===== Service Module =====
export type ModuleKey = 'mail' | 'calendar' | 'task' | 'workspace' | 'workchat';
export interface ServiceModuleItem {
  moduleKey: ModuleKey;
  enabled: boolean;
  configuration: Record<string, unknown>;
  updatedAt: string | null;
}

export async function listServiceModules(): Promise<ServiceModuleItem[]> {
  const { data } = await apiClient.get<{ items: ServiceModuleItem[] }>('/admin/service-modules');
  return Array.isArray(data?.items) ? data.items : [];
}

export async function toggleServiceModule(
  moduleKey: ModuleKey,
  payload: { enabled?: boolean; configuration?: Record<string, unknown> },
): Promise<ServiceModuleItem> {
  const { data } = await apiClient.patch<ServiceModuleItem>(`/admin/service-modules/${moduleKey}`, payload);
  return data;
}

// ===== User Role =====
// 2026-08-25 — 관리자 콘솔에서 관리자·시스템관리자도 등록·지정할 수 있어야 해서 후보를 넓혔다.
// 실제로 무엇을 고를 수 있는지는 로그인한 사람의 등급이 정한다(lib/roleRank.ts · 서버가 최종 판정).
export type AssignableRole = 'STAFF' | 'MANAGER' | 'ADMIN' | 'SYSTEM_ADMIN';
export interface UserRoleChangeResponse {
  userId: string;
  email: string;
  role: AssignableRole;
  previousRole: string;
  idempotent?: boolean;
}

export async function changeUserRole(
  userId: string,
  role: AssignableRole,
): Promise<UserRoleChangeResponse> {
  const { data } = await apiClient.patch<UserRoleChangeResponse>(`/admin/users/${userId}/role`, { role });
  return data;
}

// cycle 1-18 항목2 — 단건 사용자 생성 (ADMIN). 셀프 회원가입 차단 정책 하의 등록 동선.
export type PasswordMode = 'invite_link' | 'temp_password';
export interface CreateUserPayload {
  email: string;
  name: string;
  role?: AssignableRole;
  position?: string;
  phone?: string;
  passwordMode?: PasswordMode;
  employeeNumber?: string; // cycle 96 — 사원번호(사번 로그인용). temp_password 모드 시 백엔드 필수.
}
export interface CreateUserResponse {
  user: { id: string; email: string; name: string; role: string; position: string | null; phone: string | null };
  invite: { kind: PasswordMode; token?: string; tempPassword?: string; expiresAt: string };
}

export async function createUser(payload: CreateUserPayload): Promise<CreateUserResponse> {
  const { data } = await apiClient.post<CreateUserResponse>('/admin/users', payload);
  return data;
}

// ===== 사용자 상세 — 정보 수정 · 비밀번호 수정/초기화 =====
// chat-server `/api/v1/admin/users/:userId*` (SSOT) 를 BFF catch-all proxy 로 그대로 호출.
// 권한(role) 변경은 기존 changeUserRole 유지 — 본 API 는 프로필/계정 영역만 담당.

export interface AdminUserDetail {
  id: string;
  institutionId: string | null;
  email: string;
  name: string;
  employeeNumber: string | null;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  role: string;
  status: string;
  isActive: boolean;
  /** 비밀번호 설정 여부(해시 자체는 서버가 절대 노출 안 함). false = 초대/임시비번 대기 상태. */
  hasPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminUserProfilePatch {
  name?: string;
  email?: string;
  employeeNumber?: string | null;
  position?: string | null;
  phone?: string | null;
  isActive?: boolean;
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<{ user: AdminUserDetail }>(`/admin/users/${userId}`);
  return data.user;
}

export async function updateAdminUserProfile(
  userId: string,
  patch: AdminUserProfilePatch,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.patch<{ user: AdminUserDetail }>(`/admin/users/${userId}`, patch);
  return data.user;
}

/** 비밀번호 직접 지정 — 즉시 로그인 가능 상태로 전환 + 기존 세션 전량 만료. */
export async function setAdminUserPassword(
  userId: string,
  newPassword: string,
): Promise<{ ok: boolean; userId: string; email: string }> {
  const { data } = await apiClient.post<{ ok: boolean; userId: string; email: string }>(
    `/admin/users/${userId}/password`, { newPassword },
  );
  return data;
}

export interface AdminPasswordResetResult {
  userId: string;
  email: string;
  employeeNumber: string | null;
  invite: { kind: PasswordMode; token?: string; tempPassword?: string; expiresAt: string };
}

/** 비밀번호 초기화 — 기존 비번 폐기 후 임시비번(사번 필요) 또는 초대링크 재발급. */
export async function resetAdminUserPassword(
  userId: string,
  mode: PasswordMode,
): Promise<AdminPasswordResetResult> {
  const { data } = await apiClient.post<AdminPasswordResetResult>(
    `/admin/users/${userId}/password/reset`, { mode },
  );
  return data;
}

// ===== Cycle 20 — Institution status (grace/frozen 사전 차단용) =====
export interface InstitutionStatus {
  // Cycle 73 — 'pending_approval' 추가 (transit-state 전수 동기화). 단 pending_approval institution
  // 의 admin 은 로그인 불가라 본 union 에 실제로 도달할 일은 없으나 타입 정합성 유지.
  status: 'active' | 'pending' | 'pending_approval' | 'suspended' | 'archived' | 'frozen';
  graceUntil: string | null;
  graceActive: boolean;
  frozen: boolean;
  frozenAt: string | null;
}

export async function getInstitutionStatus(): Promise<InstitutionStatus> {
  const { data } = await apiClient.get<InstitutionStatus>('/admin/me/institution-status');
  return data;
}

// ===== Cycle 19 — Drift email per-user opt-out (Cycle 21 — updatedAt 추가) =====
export interface DriftOptOutStatus {
  optedOut: boolean;
  reason: string | null;
  since: string | null;
  updatedAt: string | null;  // Cycle 21 — 사유 마지막 변경 시점
}

export async function getDriftOptOut(): Promise<DriftOptOutStatus> {
  const { data } = await apiClient.get<DriftOptOutStatus>('/admin/me/drift-opt-out');
  return data;
}

export async function setDriftOptOut(payload: { optedOut: boolean; reason?: string | null }): Promise<{ optedOut: boolean; reason: string | null }> {
  const { data } = await apiClient.put<{ optedOut: boolean; reason: string | null }>('/admin/me/drift-opt-out', payload);
  return data;
}

// Cycle 22 — institution-wide drift email opt-out (ADMIN 변경 → 전체 ADMIN inbox 차단)
export async function getInstitutionDriftDisabled(): Promise<{ disabled: boolean }> {
  const { data } = await apiClient.get<{ disabled: boolean }>('/admin/institution/drift-email-disabled');
  return data;
}

export async function setInstitutionDriftDisabled(payload: { disabled: boolean; reason?: string | null }): Promise<{ disabled: boolean }> {
  const { data } = await apiClient.put<{ disabled: boolean }>('/admin/institution/drift-email-disabled', payload);
  return data;
}
